import { Box, Card, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Props as LegendProps } from 'recharts/types/component/DefaultLegendContent';

import { CoachMetricsAdditionalDataEnum, ShortCoachSession } from '../../../../backendsdk';
import { ScoreAvatar } from '../../../../components/ScoreAvatar';
import useProfile from '../../../../hooks/profile';
import { SECONDS_IN_DAY } from '../../../../utils/TimeFormatter';
import palette from '../../../ColorPalette';
import {
    getAccidentCount,
    getDrivenDurationHour,
    getDrivenDurationWellHour,
    getEventCounts,
} from '../../Drivers/FinesParser';
import { roundToStep } from '../../../../utils/Math';

interface CustomTooltipProps extends LegendProps {
    scoreType: string;
    additionalData: CoachMetricsAdditionalDataEnum;
    onScoreCard?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = (props: CustomTooltipProps) => {
    const { payload } = props;
    const { t } = useTranslation();
    const event_count_data: Record<string, number> = {};
    let drive_duration = '';
    let well_drive_duration = '';
    let accident_count = 0;

    if (payload && payload.length) {
        const hasAdditionalData = payload.length > 1;
        return (
            <Card
                id="metrics-tooltip"
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1, direction: 'ltr' }}
            >
                {payload.map((p, idx) => {
                    if (p.payload) {
                        Object.assign(event_count_data, getEventCounts(p.payload.metadata));
                        drive_duration = roundToStep(getDrivenDurationHour(p.payload.metadata), 0.5).toString();
                        well_drive_duration = roundToStep(getDrivenDurationWellHour(p.payload.metadata), 0.5).toString();
                        accident_count = getAccidentCount(p.payload.metadata);
                        if (accident_count > 0) {
                            event_count_data['accident'] = accident_count;
                        }
                        const name = p.name as string;
                        const score = p.payload[name];
                        const labelKey =
                            name === 'score' && props.onScoreCard
                                ? `driver_metric_header_${name}`
                                : `metric_header_${name}`;
                        return (
                            <Box
                                key={p.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: hasAdditionalData ? 'row' : 'column',
                                    justifyContent: hasAdditionalData ? 'space-between' : 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    mb: hasAdditionalData && idx !== payload.length - 1 ? 0.5 : 0,
                                }}
                            >
                                <Typography
                                    fontSize={12}
                                    sx={{ fontWeight: 'bold', mb: 0.5, mr: hasAdditionalData ? 1 : 0 }}
                                >
                                    {t(`content.coach.add_item.metrics.${labelKey}`, {
                                        score_type: t(
                                            `content.coach.add_item.metrics.${props.scoreType}_score_definite`,
                                        ),
                                    })}
                                </Typography>
                                <ScoreAvatar score={score} />
                            </Box>
                        );
                    }
                })}

                <Box
                    sx={{
                        width: '100%',
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                >
                    <Typography
                        fontSize={12}
                        fontWeight={500}
                        sx={{ color: 'text.primary', mb: 1, width: '100%', textAlign: 'start' }}
                    >
                        {t('content.safety.scores.components.duration_title')}
                    </Typography>

                    <Typography fontSize={12} sx={{ width: '100%', textAlign: 'start' }}>
                        {'• '}
                        {t('content.safety.scores.components.drive_duration', { drive_duration })}
                    </Typography>
                    <Typography fontSize={12} sx={{ width: '100%', textAlign: 'start' }}>
                        {'• '}
                        {t('content.safety.scores.components.well_drive_duration', {
                            well_drive_duration,
                            drive_duration,
                        })}
                    </Typography>

                    {Object.entries(event_count_data).map(([event_type, event_count]) => (
                        <Typography key={event_type} fontSize={12} sx={{ width: '100%', textAlign: 'start' }}>
                            {'• '}
                            {t('content.safety.scores.components.event_count', {
                                count: event_count,
                                event_type: t(`content.safety.scores.components.${event_type}`).toLowerCase(),
                            })}
                        </Typography>
                    ))}
                </Box>
            </Card>
        );
    }
    return null;
};

interface DataPoint {
    name: number;
    score: number | null;
}

interface MetricsGraphProps {
    metricChartData: DataPoint[];
    scoreType: 'safety' | 'wellness';
    additionalData: CoachMetricsAdditionalDataEnum;
    onScoreCard?: boolean;
    isPresenting?: boolean;
    coachingSessions?: ShortCoachSession[];
}

const MetricsGraph: React.FC<MetricsGraphProps> = ({
    metricChartData,
    scoreType,
    additionalData,
    onScoreCard,
    isPresenting,
    coachingSessions,
}: MetricsGraphProps) => {
    const { t } = useTranslation();
    const { profile } = useProfile();

    return (
        <Box
            data-testid="metrics-graph-container"
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {!onScoreCard ? (
                <Typography data-testid="metrics-header" fontSize={isPresenting ? 18 : undefined}>
                    {t('content.coach.add_item.metrics.metrics_header', {
                        score_type: t(`content.coach.add_item.metrics.${scoreType}_score_definite`),
                        date_from: dayjs.unix(metricChartData[0].name).format(profile.shortDateFormat),
                        date_to: dayjs
                            .unix(metricChartData[metricChartData.length - 1].name)
                            .format(profile.shortDateFormat),
                    })}
                </Typography>
            ) : null}
            <ResponsiveContainer width="100%" height="100%" className="chart-container">
                <LineChart
                    id="metrics-graph"
                    data={metricChartData}
                    margin={{
                        top: 5,
                        right: 20,
                        left: -20,
                        bottom: 0,
                    }}
                >
                    <CartesianGrid />
                    <XAxis
                        type="number"
                        tick={{ fontSize: 12 }}
                        dataKey="name"
                        ticks={metricChartData.map((d) => d.name)}
                        tickFormatter={(t) => dayjs.unix(t).format(profile.shortDateFormat)}
                        domain={['dataMin', 'dataMax']}
                        interval={0}
                    />
                    <YAxis tick={{ fontSize: 12 }} interval={0} tickCount={11} domain={[0, 100]} />
                    <Tooltip
                        content={
                            <CustomTooltip
                                scoreType={scoreType}
                                additionalData={additionalData}
                                onScoreCard={onScoreCard}
                            />
                        }
                    />
                    {additionalData !== CoachMetricsAdditionalDataEnum.None ? (
                        <>
                            <Line
                                connectNulls
                                isAnimationActive={false}
                                type="monotone"
                                dataKey={`${additionalData}_score`}
                                stroke={palette.neutral[400]}
                                strokeWidth={2}
                            />
                        </>
                    ) : null}
                    <Line
                        connectNulls
                        isAnimationActive={false}
                        type="monotone"
                        dataKey="score"
                        stroke={palette.accent}
                        strokeWidth={2}
                    />
                    {(coachingSessions || []).map((session) => (
                        <ReferenceLine
                            key={session.session_id}
                            x={Math.floor(session.completed_at / SECONDS_IN_DAY) * SECONDS_IN_DAY}
                            stroke={palette.neutral[400]}
                            strokeDasharray="4 4"
                            strokeWidth={2}
                        />
                    ))}
                    {!!coachingSessions?.length ? (
                        <Line strokeDasharray="4 4" name="coaching" dataKey="null" stroke={palette.neutral[400]} />
                    ) : null}
                    {!!coachingSessions?.length || additionalData !== CoachMetricsAdditionalDataEnum.None ? (
                        <Legend
                            wrapperStyle={{ marginLeft: 20 }}
                            iconType="plainline"
                            formatter={(label) => {
                                if (label === 'score' && onScoreCard) {
                                    return t(`content.coach.add_item.metrics.driver_${label}`);
                                }
                                return t(`content.coach.add_item.metrics.${label}`);
                            }}
                        />
                    ) : null}
                </LineChart>
            </ResponsiveContainer>
            {!onScoreCard ? (
                <Typography data-testid="metrics-footer" fontSize={isPresenting ? 14 : 12} sx={{ mt: 1 }}>
                    {t(`content.metrics.${scoreType}_tooltip`)}
                </Typography>
            ) : null}
        </Box>
    );
};

export default MetricsGraph;
